// Composer Studio — 선택 노드 4변 경계 드래그 리사이즈 오버레이(엑셀/한글 셀 경계식).
//
// 선택된 노드의 4변(상/우/하/좌) 바깥 경계에 얇은 hit strip을 띄운다. strip 위에서 pointer 드래그하면
// 그 축의 크기(가로=width, 세로=height; 결재란은 cellWidth/signatureHeight)를 조정한다. 시각 핸들(■)은
// 렌더하지 않는다 — 셀 경계 드래그(TableEdgeResizeOverlay)와 동일한 "테두리를 잡아 끈다" 감각.
// 드래그 중에는 onPreview로 연속 미리보기(히스토리 미기록), pointerup에 onCommit으로 1개 히스토리
// 엔트리를 커밋한다(ResizeHandles 승계).
//
// (A) 이동 드래그(DragLayer의 NodeDragRing)와의 분리 — 핵심
//   NodeDragRing은 노드 테두리 "안쪽" 7px strip을 이동(cursor:move) 영역으로 잡는다(전 노드 상시).
//   본 오버레이는 테두리 "바깥쪽" 영역만 리사이즈(cursor:ew/ns-resize)로 잡아 공간이 겹치지 않는다.
//   → 안쪽=이동, 바깥쪽=리사이즈로 커서까지 구분된다(선택 노드에 한해 리사이즈 존 활성).
//
// (B) 어느 변을 잡아도 크기 조정 — 정렬(alignH/alignV)에 따른 자유변 제한 없음.
//   좌/상변은 바깥으로 끌면 grow(Δ=-포인터델타), 우/하변은 바깥으로 끌면 grow(Δ=+포인터델타).
//   좌변 드래그로 폭이 줄면 위치는 기존 정렬 규칙(justify/align)이 알아서 배치한다.
//
// (C) fill/hug 노드 — 드래그 시작 시 실측 px(zoom 보정)로 그 축만 구체값 전환(ResizeHandles 승계):
//   width가 number면 저장값, 아니면(fill/hug/미지정) 실측 rect.width를 시작값으로. height도 동일
//   (세로는 layout.height — 속성패널 "고정"과 같은 필드. minHeight 아님, 2026-07-04 fill-resize 통일).
//
// (D) 결재란(special approvalBlock) — layout 대신 props.approvalConfig에 매핑:
//   가로: cellWidth = max(MIN, startCellWidth + Δw/셀수), 세로: signatureHeight = max(MIN, startSig + Δh).
//
// 엔진 DOM은 무수정. 선택 노드의 [data-composer-node-id] rect를 읽어 strip 위치를 잡는다. branch 노드는
// EditorPage에서 게이트(kind!=='branch'), 표 노드는 TableEdgeResizeOverlay가 담당해 여기서 제외한다.

import { useCallback, useEffect, useRef } from 'react';
import type { ComposerNode } from '@safetyhub/report-composer';
import { cssEscape, getNodeRect } from './canvasRect';

const MIN_PX = 8;

/** 테두리 바깥으로 뻗는 리사이즈 hit strip 두께(px). 이동(안쪽 7px)과 공간이 겹치지 않게 바깥 전용. */
const EDGE = 6;

/** 결재란 config의 CSS 기본값 (approval-form.css 기준) */
const DEFAULT_CELL_WIDTH = 80;
const DEFAULT_SIGNATURE_HEIGHT = 50;

type Edge = 'top' | 'right' | 'bottom' | 'left';

function getNodeEl(canvas: HTMLElement, nodeId: string): HTMLElement | null {
  return canvas.querySelector<HTMLElement>(`[data-composer-node-id="${cssEscape(nodeId)}"]`);
}

/** 결재란 special 판정 */
function isApprovalNode(node: ComposerNode): boolean {
  return node.kind === 'special' && node.special === 'approvalBlock';
}

/** 결재란 셀 개수 측정(.approval-cell). 0이면 1로 가드. */
function countApprovalCells(canvas: HTMLElement, nodeId: string): number {
  const el = getNodeEl(canvas, nodeId);
  if (!el) return 1;
  const n = el.querySelectorAll('.approval-cell').length;
  return n > 0 ? n : 1;
}

/** 결재란 서명란(.signature-area) 측정 높이(px). 없으면 null. */
function measureSignatureHeight(canvas: HTMLElement, nodeId: string): number | null {
  const el = getNodeEl(canvas, nodeId);
  if (!el) return null;
  const sig = el.querySelector<HTMLElement>('.signature-area');
  if (!sig) return null;
  return Math.round(sig.getBoundingClientRect().height);
}

export function NodeEdgeResizeOverlay({
  canvas,
  node,
  /** 캔버스 줌 배율(미지정=1). 포인터 화면 델타를 논리 px로 환산하는 데 쓴다. */
  zoom = 1,
  /** 드래그 중 연속 미리보기(히스토리 미기록) */
  onPreview,
  /** pointerup 시 1개 히스토리 엔트리 커밋 */
  onCommit,
}: {
  canvas: HTMLElement | null;
  node: ComposerNode;
  zoom?: number;
  onPreview: (patch: Partial<ComposerNode>) => void;
  onCommit: (patch: Partial<ComposerNode>) => void;
}) {
  // 드래그 시작 시점의 기준값 보관
  const startRef = useRef<{
    pointerX: number;
    pointerY: number;
    axis: 'width' | 'height';
    /** grow 방향 부호 — 좌/상=-1(바깥=음의 델타로 grow), 우/하=+1. */
    sign: 1 | -1;
    startWidth: number;
    startHeight: number;
    approval: boolean;
    cells: number;
    startCellWidth: number;
    startSigHeight: number;
  } | null>(null);

  // 드래그 중 등록된 window 리스너 해제기(드래그 도중 언마운트 시 누수 방지 — ResizeHandles와 동일).
  const dragCleanupRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    return () => {
      dragCleanupRef.current?.();
      dragCleanupRef.current = null;
    };
  }, []);

  const beginResize = useCallback(
    (edge: Edge, e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!canvas) return;
      const rect = getNodeRect(canvas, node.id);
      if (!rect) return;

      const axis: 'width' | 'height' = edge === 'left' || edge === 'right' ? 'width' : 'height';
      const sign: 1 | -1 = edge === 'left' || edge === 'top' ? -1 : 1;

      const approval = isApprovalNode(node);

      // 일반 노드 시작 px — fill/hug(number 아님)면 실측 rect로 구체값 전환(ResizeHandles 승계).
      const curWidth =
        typeof node.layout.width === 'number' ? node.layout.width : Math.round(rect.width);
      const curHeight =
        typeof node.layout.height === 'number' ? node.layout.height : Math.round(rect.height);

      // 결재란 시작값 (config 우선, 없으면 측정)
      const config =
        node.kind === 'special'
          ? ((node.props?.approvalConfig as
              | { cellWidth?: number; signatureHeight?: number }
              | undefined) ?? {})
          : {};
      const cells = approval ? countApprovalCells(canvas, node.id) : 1;
      const startCellWidth =
        config.cellWidth ?? (approval ? Math.round(rect.width / cells) : DEFAULT_CELL_WIDTH);
      const startSigHeight =
        config.signatureHeight ??
        (approval
          ? (measureSignatureHeight(canvas, node.id) ?? DEFAULT_SIGNATURE_HEIGHT)
          : DEFAULT_SIGNATURE_HEIGHT);

      startRef.current = {
        pointerX: e.clientX,
        pointerY: e.clientY,
        axis,
        sign,
        startWidth: curWidth,
        startHeight: curHeight,
        approval,
        cells,
        startCellWidth,
        startSigHeight,
      };

      /** 현재 포인터 기준 patch 계산 */
      const computePatch = (ev: PointerEvent): Partial<ComposerNode> => {
        const s = startRef.current!;
        if (s.axis === 'width') {
          // 화면 델타 ÷ zoom = 논리 px(node.layout.width는 미스케일 논리값). 좌변=-, 우변=+.
          const dw = (s.sign * (ev.clientX - s.pointerX)) / zoom;
          if (s.approval) {
            const nextCellWidth = Math.max(MIN_PX, Math.round(s.startCellWidth + dw / s.cells));
            return mergeApprovalConfig(node, { cellWidth: nextCellWidth });
          }
          const next = Math.max(MIN_PX, Math.round(s.startWidth + dw));
          return { layout: { ...node.layout, width: next } } as Partial<ComposerNode>;
        }
        // height — 상변=-, 하변=+ (화면 델타 ÷ zoom = 논리 px).
        const dh = (s.sign * (ev.clientY - s.pointerY)) / zoom;
        if (s.approval) {
          const nextSig = Math.max(MIN_PX, Math.round(s.startSigHeight + dh));
          return mergeApprovalConfig(node, { signatureHeight: nextSig });
        }
        const next = Math.max(MIN_PX, Math.round(s.startHeight + dh));
        return { layout: { ...node.layout, height: next } } as Partial<ComposerNode>;
      };

      const onMove = (ev: PointerEvent) => {
        if (!startRef.current) return;
        onPreview(computePatch(ev));
      };

      const removeListeners = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        dragCleanupRef.current = null;
      };

      const onUp = (ev: PointerEvent) => {
        const s = startRef.current;
        if (!s) {
          removeListeners();
          return;
        }
        // ⚠️ patch 계산은 startRef를 비우기 **전에** — computePatch가 startRef.current!를 읽으므로
        // 먼저 null로 만들면 pointerup에서 TypeError로 onCommit이 죽어 히스토리 미기록(undo 불능).
        const patch = computePatch(ev);
        startRef.current = null;
        removeListeners();
        onCommit(patch);
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      dragCleanupRef.current = removeListeners;
    },
    [canvas, node, zoom, onPreview, onCommit],
  );

  if (!canvas) return null;
  const rect = getNodeRect(canvas, node.id);
  if (!rect) return null;

  const stripBase: React.CSSProperties = {
    position: 'absolute',
    pointerEvents: 'auto',
    touchAction: 'none',
    // 배경 없음(투명) — 시각 핸들 미표시, 커서로만 리사이즈 가능 영역을 알린다.
    zIndex: 41,
  };

  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {/* 좌변 — 테두리 바깥(좌측) 세로 strip */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="너비 조절(좌)"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => beginResize('left', e)}
        style={{
          ...stripBase,
          top: rect.top,
          left: rect.left - EDGE,
          width: EDGE,
          height: rect.height,
          cursor: 'ew-resize',
        }}
      />
      {/* 우변 — 테두리 바깥(우측) 세로 strip */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="너비 조절(우)"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => beginResize('right', e)}
        style={{
          ...stripBase,
          top: rect.top,
          left: rect.left + rect.width,
          width: EDGE,
          height: rect.height,
          cursor: 'ew-resize',
        }}
      />
      {/* 상변 — 테두리 바깥(위) 가로 strip */}
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label="높이 조절(상)"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => beginResize('top', e)}
        style={{
          ...stripBase,
          top: rect.top - EDGE,
          left: rect.left,
          width: rect.width,
          height: EDGE,
          cursor: 'ns-resize',
        }}
      />
      {/* 하변 — 테두리 바깥(아래) 가로 strip */}
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label="높이 조절(하)"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => beginResize('bottom', e)}
        style={{
          ...stripBase,
          top: rect.top + rect.height,
          left: rect.left,
          width: rect.width,
          height: EDGE,
          cursor: 'ns-resize',
        }}
      />
    </div>
  );
}

/**
 * 결재란 노드의 props.approvalConfig에 부분 패치를 병합한 patch 객체를 만든다.
 * 기존 props/config를 보존하며 지정 키만 덮어쓴다.
 */
function mergeApprovalConfig(
  node: ComposerNode,
  partial: { cellWidth?: number; signatureHeight?: number },
): Partial<ComposerNode> {
  const prevProps = node.kind === 'special' ? (node.props ?? {}) : {};
  const prevConfig = (prevProps.approvalConfig as Record<string, unknown> | undefined) ?? {};
  return {
    props: {
      ...prevProps,
      approvalConfig: { ...prevConfig, ...partial },
    },
  } as Partial<ComposerNode>;
}
